import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRecorder, formatTime } from "@/lib/useRecorder";
import {
  Microphone,
  Stop,
  Play,
  ArrowClockwise,
  ArrowRight,
  CheckCircle,
  Waveform as WaveIcon,
  Quotes,
  SpeakerHigh,
  EyeSlash,
  PauseCircle,
} from "@phosphor-icons/react";
import { INTERVIEW } from "@/constants/testIds";
import { makeT } from "@/lib/i18n";
import { SeniorityBadge } from "@/components/SeniorityBadge";
import { extForMime } from "@/lib/useRecorder";

// Fixed, approved warm-up library. These are the ONLY warm-up questions that
// may be used — do NOT add, modify, or generate additional warm-up questions
// without explicit approval. One is picked at random for each session. The
// warm-up is a client-side practice exercise only; the response is NEVER
// sent to the backend and cannot appear in transcripts, scores, reports,
// coaching, or the study plan.
const WARMUP_POOL = [
  "Describe your favorite season of the year and explain why you like it.",
  "Describe your favorite type of food and what you enjoy about it.",
  "Describe a place that you would recommend someone visit and explain why.",
  "Describe a piece of technology that you use regularly and why you find it useful.",
  "Describe your ideal vacation destination and what makes it appealing to you.",
];

const warmupKey = (id) => `mec_warmup_${id}`;
// Backend allows 2 × 20 s attempts; give it headroom, then give up on audio.
const TTS_CLIENT_TIMEOUT_MS = 45000;


export default function Interview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Snapshot the form state we were launched with so the "Reiniciar entrevista"
  // button can hand it back to the Upload screen — preserves name, resume,
  // JD selection, voice, etc. without re-uploading anything.
  const launchState = useRef(location.state?.formState || null);

  const restartInterview = () => {
    // Stop any playing TTS / recorder before leaving.
    try { rec.stop && rec.stop(); } catch { /* noop */ }
    try {
      const el = audioElRef.current;
      if (el) { el.pause(); el.currentTime = 0; }
    } catch { /* noop */ }
    if (!window.confirm(t("iv.restartConfirm"))) return;
    // Fire-and-forget deletion of the abandoned session — user shouldn't
    // wait on the network to leave the page. Completed sessions are kept
    // by the backend regardless.
    api.delete(`/sessions/${sessionId}`).catch(() => { /* noop */ });
    navigate("/", { state: { restore: launchState.current || {} } });
  };
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [revealText, setRevealText] = useState(false);
  const rec = useRecorder();

  // Auto-advance timer: after a successful submission we wait ~2s so the
  // candidate registers that their answer was saved, then jump to the next
  // question. No banner, no undo — the pause itself is the affordance.
  const autoAdvanceTimerRef = useRef(null);
  const clearAutoAdvanceTimer = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearAutoAdvanceTimer(), [clearAutoAdvanceTimer]);

  // Warm-up state — entirely client-side. sessionStorage keeps the flag
  // across page reloads for the same session but never persists to the
  // backend, so no warm-up data can leak into any interview artifact.
  const warmupRec = useRecorder();
  const [warmupDone, setWarmupDone] = useState(() => {
    try {
      return typeof window !== "undefined" && window.sessionStorage.getItem(warmupKey(sessionId)) === "1";
    } catch {
      return false;
    }
  });
  const [warmupQuestion] = useState(
    () => WARMUP_POOL[Math.floor(Math.random() * WARMUP_POOL.length)]
  );
  const warmupAudioUrl = useMemo(
    () => (warmupRec.blob ? URL.createObjectURL(warmupRec.blob) : null),
    [warmupRec.blob]
  );
  const finishWarmup = () => {
    try { warmupRec.stop && warmupRec.stop(); } catch { /* noop */ }
    try { window.sessionStorage.setItem(warmupKey(sessionId), "1"); } catch { /* noop */ }
    setWarmupDone(true);
  };

  // TTS playback state. Only the original approved English question is voiced.
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const audioElRef = useRef(null);
  const ttsCacheRef = useRef({}); // question index -> object URL
  const autoplayedRef = useRef(new Set());
  // Pending autoplay timer for the current question — used so a Record tap
  // can cancel it and prevent TTS from starting after recording has begun.
  const pendingAutoplayRef = useRef(null);
  const cancelPendingAutoplay = () => {
    if (pendingAutoplayRef.current) {
      clearTimeout(pendingAutoplayRef.current);
      pendingAutoplayRef.current = null;
    }
  };

  const audioUrl = useMemo(
    () => (rec.blob ? URL.createObjectURL(rec.blob) : null),
    [rec.blob]
  );
  // Whenever `audioUrl` changes (new recording OR blob cleared) revoke the
  // previous object URL so the underlying Blob can be garbage-collected.
  useEffect(() => {
    if (!audioUrl) return undefined;
    return () => URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  useEffect(() => {
    api
      .get(`/sessions/${sessionId}`)
      .then((r) => {
        setSession(r.data);
        // Jump to first unanswered question
        const answered = new Set((r.data.answers || []).map((a) => a.question_index));
        const first = r.data.questions.findIndex((_, i) => !answered.has(i));
        setIdx(first === -1 ? r.data.questions.length - 1 : first);
      })
      .catch(() => {
        toast.error(t("iv.loadFail"));
        navigate("/");
      });
  }, [sessionId, navigate]);

  const question = session?.questions?.[idx];
  const total = session?.questions?.length || 0;
  const answers = session?.answers || [];
  const currentAnswer = answers.find((a) => a.question_index === idx);
  const t = makeT(session?.interface_language || session?.language || "en");

  const submit = async () => {
    if (!rec.blob) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      // Build the filename from the ACTUAL blob mime type — iOS Safari
      // records audio/mp4 (not webm), and Whisper needs the extension to
      // match the bytes it receives.
      const ext = extForMime(rec.blob.type);
      fd.append("audio", rec.blob, `answer.${ext}`);
      fd.append("question_index", String(idx));
      const { data } = await api.post(`/sessions/${sessionId}/answer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(data.transcript);
      // Audio has been successfully transcribed and evaluated by the
      // backend. Immediately release the local audio Blob so it cannot
      // remain in memory beyond the minimum time technically needed for
      // transcription/upload. Only the transcript + evaluation persist.
      rec.reset();
      // Refresh session state
      const s = await api.get(`/sessions/${sessionId}`);
      setSession(s.data);
      toast.success(t("iv.answerSaved"));
      // Auto-advance to the next question after a short pause, unless this
      // was the final question (user needs to click Finish there).
      const totalNow = s.data?.questions?.length || 0;
      if (idx < totalNow - 1) {
        autoAdvanceTimerRef.current = setTimeout(() => {
          autoAdvanceTimerRef.current = null;
          setIdx((i) => (i < totalNow - 1 ? i + 1 : i));
        }, 2000);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || t("iv.submitFail"));
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.post(`/sessions/${sessionId}/complete`);
      navigate(`/report/${sessionId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv.reportFail"));
      setFinishing(false);
    }
  };

  // Fetch/cache TTS for the approved English question. In-flight requests are
  // deduplicated so prefetch and play requests for the same question share one
  // network call.
  const ttsInflightRef = useRef({}); // question index -> Promise<url>
  const fetchTts = (i) => {
    const key = String(i);
    if (ttsCacheRef.current[key]) return Promise.resolve(ttsCacheRef.current[key]);
    if (ttsInflightRef.current[key]) return ttsInflightRef.current[key];
    const fd = new FormData();
    fd.append("question_index", String(i));
    fd.append("variant", "original");
    const p = api
      .post(`/sessions/${sessionId}/tts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TTS_CLIENT_TIMEOUT_MS,
      })
      .then(({ data }) => {
        const bin = atob(data.audio_base64);
        const bytes = new Uint8Array(bin.length);
        for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
        const blob = new Blob([bytes], { type: data.mime || "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        ttsCacheRef.current[key] = url;
        return url;
      })
      .finally(() => {
        delete ttsInflightRef.current[key];
      });
    ttsInflightRef.current[key] = p;
    return p;
  };

  const prefetchNextTts = (current) => {
    const next = current + 1;
    if (!session?.questions?.[next]) return;
    fetchTts(next).catch((e) => console.warn("TTS prefetch failed", next, e?.message));
  };

  const playQuestion = async (i = idx, { silent = false } = {}) => {
    if (!session?.questions?.[i]) return;
    try {
      setTtsLoading(true);
      const url = await fetchTts(i);
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = url;
        audioElRef.current.currentTime = 0;
        await audioElRef.current.play();
        setTtsPlaying(true);
      }
    } catch (e) {
      console.warn("TTS play failed", e);
      const isAutoplayBlock = e?.name === "NotAllowedError";
      if (!isAutoplayBlock) {
        // Audio is unavailable (timeout / backend gave up / network): never
        // block the interview — make the question text readable and move on.
        setRevealText(true);
        toast.error(t("iv.audioUnavailable"));
      } else if (!silent) {
        toast.error(t("iv.audioPlayFail"));
      }
    } finally {
      setTtsLoading(false);
    }
  };

  const stopQuestion = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.currentTime = 0;
    }
    setTtsPlaying(false);
  };

  // Wrap rec.start so any interviewer audio currently playing is silenced
  // immediately — recording always takes priority over TTS playback so the
  // interviewer voice can't bleed into the candidate's answer.
  const startRecording = () => {
    cancelPendingAutoplay();
    stopQuestion();
    rec.start();
    // Warm the next approved question while the candidate is answering.
    prefetchNextTts(idx);
  };

  // Reset per-question UI state when index changes; auto-play the question once.
  useEffect(() => {
    setRevealText(false);
    setTranscript(null);
    rec.reset();
    stopQuestion();
    // Any pending auto-advance is bound to the previous question — clear it
    // if the user navigated (or the timer fired) so it can't race.
    clearAutoAdvanceTimer();
    if (!session || !session.questions?.[idx]) return;
    // Don't autoplay while the warm-up gate is still on screen — the main
    // interview <audio> element isn't mounted yet, so playback would fail
    // silently AND the question would be marked "already autoplayed",
    // preventing Q1 from ever autoplaying once the user continues.
    if (!warmupDone) return;
    // Auto-play once per index per mount, but only if user hasn't answered already.
    if (autoplayedRef.current.has(idx)) return;
    const answered = (session.answers || []).some((a) => a.question_index === idx);
    if (answered) return;
    autoplayedRef.current.add(idx);
    const key = String(idx);
    // Prefetched audio plays immediately; otherwise keep the short settle delay.
    const delay = ttsCacheRef.current[key] ? 0 : 400;
    pendingAutoplayRef.current = setTimeout(() => {
      pendingAutoplayRef.current = null;
      playQuestion(idx, { silent: true });
    }, delay);
    return () => {
      if (pendingAutoplayRef.current) {
        clearTimeout(pendingAutoplayRef.current);
        pendingAutoplayRef.current = null;
      }
    };
  }, [idx, session?.session_id, warmupDone]);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]">
        {t("iv.loading")}
      </div>
    );
  }

  // Show the mandatory warm-up gate before the first real question. Once the
  // user completes it (or if they've already answered a real question, e.g.
  // after refreshing mid-interview) we fall through to the assessment UI.
  if (!warmupDone && (session.answers || []).length === 0) {
    return (
      <WarmupGate
        question={warmupQuestion}
        rec={warmupRec}
        audioUrl={warmupAudioUrl}
        onContinue={finishWarmup}
        onRecordStart={() => prefetchNextTts(-1)}
        voice={session.voice || "female"}
        t={t}
      />
    );
  }

  const answeredCount = answers.length;
  const allAnswered = answeredCount === total;
  const hasAnswerForCurrent = Boolean(currentAnswer);
  const hideText = Boolean(session.hide_text) && !revealText && !hasAnswerForCurrent;

  return (
    <div className="min-h-screen" data-testid={INTERVIEW.container}>
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-8 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="My English Coach Interview Simulator logo"
              className="h-[63px] w-[63px] object-contain"
            />
            <div className="leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-semibold tracking-tight">My English Coach Interview Simulator</span>
              </div>
            </div>
            {session?.seniority?.level && (
              <div className="hidden sm:block ml-2">
                <SeniorityBadge
                  seniority={session.seniority}
                  lang={session.interface_language || session.language || "en"}
                  testId="interview-seniority-badge"
                />
              </div>
            )}
          </div>
          <ProgressRing
            current={idx + 1}
            total={total}
            answered={answeredCount}
            testId={INTERVIEW.progressLabel}
            t={t}
          />
          <Button
            data-testid={INTERVIEW.finishBtn}
            disabled={!allAnswered || finishing}
            onClick={finish}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white disabled:opacity-40"
          >
            {finishing ? t("iv.finish.busy") : t("iv.finish")}
          </Button>
        </div>
        <div className="h-1 w-full bg-[color:var(--border-stone)]">
          <div
            className="h-full bg-[color:var(--brand)] transition-all"
            style={{ width: `${(answeredCount / Math.max(1, total)) * 100}%` }}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-8 md:px-12 py-12 pb-32 sm:pb-12">
        <section>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">
              {hideText
                ? t("iv.questionN", idx + 1)
                : question?.type === "standard"
                ? t("iv.standardQuestion")
                : question?.focus || (session.has_job_description ? t("iv.roleQuestion") : t("iv.resumeQuestion"))}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                data-testid={INTERVIEW.playQuestion || "play-question-btn"}
                onClick={() => (ttsPlaying ? stopQuestion() : playQuestion(idx))}
                disabled={ttsLoading}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 px-4 py-2 text-sm disabled:opacity-50"
              >
                {ttsLoading ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-[color:var(--brand)] animate-pulse" />
                    {t("iv.loading")}
                  </>
                ) : ttsPlaying ? (
                  <>
                    <PauseCircle weight="fill" size={16} /> {t("iv.stopQ")}
                  </>
                ) : (
                  <>
                    <SpeakerHigh weight="fill" size={16} /> {t("iv.playQ")}
                  </>
                )}
              </button>
              <button
                type="button"
                data-testid="restart-interview-btn"
                onClick={restartInterview}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)] hover:text-[color:var(--text-primary)] px-4 py-2 text-sm"
              >
                <ArrowClockwise size={16} /> {t("iv.restartInterview")}
              </button>
              <span className="text-xs mono text-[color:var(--text-secondary)] uppercase tracking-widest">
                {session.voice === "male" ? t("iv.maleVoice") : t("iv.femaleVoice")}
              </span>
            </div>
          </div>
          {hideText ? (
            <div className="mt-2 rounded-3xl border border-dashed border-[color:var(--brand)]/40 bg-[color:var(--brand)]/5 p-8">
              <div className="flex items-center gap-3 text-[color:var(--brand)]">
                <EyeSlash weight="duotone" size={22} />
                <div className="font-semibold tracking-tight">{t("iv.hiddenH")}</div>
              </div>
              <p className="mt-3 text-[color:var(--text-secondary)] leading-relaxed max-w-xl">
                {t("iv.hiddenP")}
              </p>
              <button
                data-testid="reveal-question-text"
                onClick={() => setRevealText(true)}
                className="mt-5 text-sm text-[color:var(--brand)] hover:underline"
              >
                {t("iv.reveal")}
              </button>
            </div>
          ) : (
            <h1
              className="mt-2 text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-[1.15]"
              data-testid="question-text"
            >
              <Quotes size={28} weight="fill" className="inline-block text-[color:var(--brand)] mr-2 -translate-y-1" />
              {question?.text}
            </h1>
          )}

          <audio
            ref={audioElRef}
            onEnded={() => setTtsPlaying(false)}
            onPause={() => setTtsPlaying(false)}
            className="hidden"
          />

          <div className="mt-10 rounded-3xl border border-[color:var(--border-stone)] bg-white p-8">
            {currentAnswer ? (
              <AnsweredView
                answer={currentAnswer}
                t={t}
                isFinal={idx === total - 1 && allAnswered}
              />
            ) : (
              <RecorderView
                t={t}
                recording={rec.recording}
                seconds={rec.seconds}
                blob={rec.blob}
                audioUrl={audioUrl}
                error={rec.error}
                onStart={startRecording}
                onStop={rec.stop}
                onReset={rec.reset}
                onSubmit={submit}
                submitting={submitting}
                transcript={transcript}
              />
            )}
          </div>

          <div className="mt-8 flex items-center justify-end">
            {idx < total - 1 ? (
              <Button
                data-testid={INTERVIEW.nextBtn}
                onClick={() => setIdx(idx + 1)}
                disabled={!currentAnswer}
                variant="outline"
                className="rounded-full border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 disabled:opacity-40"
              >
                {t("iv.skipNext")}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            ) : allAnswered ? (
              <Button
                data-testid="finish-inline-btn"
                onClick={finish}
                disabled={finishing}
                className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white disabled:opacity-40"
              >
                {finishing ? t("iv.finish.busy") : t("iv.finish")}
                {!finishing && <ArrowRight className="ml-2" size={16} />}
              </Button>
            ) : (
              <div className="text-sm text-[color:var(--text-secondary)]">
                {t("iv.lastHint")}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Mobile-only sticky bar with a contextual primary CTA that mirrors
          the state of the recorder. Hidden on ≥sm. Safe-area padding for
          notched iPhones. */}
      <div
        data-testid="interview-mobile-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[color:var(--border-stone)] px-4 py-3 sm:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {rec.recording ? (
          <Button
            data-testid="interview-mobile-stop"
            onClick={rec.stop}
            className="w-full rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white py-6 text-base"
          >
            <Stop weight="fill" className="mr-2" size={18} /> {t("iv.stop")}
          </Button>
        ) : allAnswered && currentAnswer ? (
          <Button
            data-testid="interview-mobile-finish"
            onClick={finish}
            disabled={finishing}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base disabled:opacity-40"
          >
            {finishing ? t("iv.finish.busy") : t("iv.finish")}
            {!finishing && <ArrowRight className="ml-2" size={18} />}
          </Button>
        ) : rec.blob ? (
          <div className="flex items-center gap-2">
            <button
              data-testid="interview-mobile-rerecord"
              onClick={rec.reset}
              className="shrink-0 h-12 w-12 rounded-full border border-[color:var(--border-stone)] flex items-center justify-center text-[color:var(--text-secondary)]"
              aria-label={t("iv.rerecord")}
            >
              <ArrowClockwise size={18} />
            </button>
            <Button
              data-testid="interview-mobile-submit"
              onClick={submit}
              disabled={submitting}
              className="flex-1 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
            >
              {submitting ? t("iv.submitting") : t("iv.submit")}
              {!submitting && <ArrowRight className="ml-2" size={18} />}
            </Button>
          </div>
        ) : idx < total - 1 && answers.find((a) => a.question_index === idx) ? (
          <Button
            data-testid="interview-mobile-next"
            onClick={() => setIdx(idx + 1)}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            {t("iv.skipNext")} <ArrowRight className="ml-2" size={18} />
          </Button>
        ) : (
          <Button
            data-testid="interview-mobile-record"
            onClick={startRecording}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            <Microphone weight="fill" className="mr-2" size={18} />
            {t("iv.tapMic")}
          </Button>
        )}
      </div>
    </div>
  );
}

function RecorderView({
  t,
  recording,
  seconds,
  blob,
  audioUrl,
  error,
  onStart,
  onStop,
  onReset,
  onSubmit,
  submitting,
  transcript,
}) {
  return (
    <div>
      <div className="flex items-center gap-6">
        <div className="relative">
          {recording && (
            <>
              <span className="absolute inset-0 rounded-full bg-[color:var(--brand)]/30 rec-ring" />
              <span
                className="absolute inset-0 rounded-full bg-[color:var(--brand)]/20 rec-ring"
                style={{ animationDelay: "600ms" }}
              />
            </>
          )}
          {!recording && !blob ? (
            <button
              data-testid={INTERVIEW.recordBtn}
              onClick={onStart}
              className="relative h-20 w-20 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_10px_30px_-10px_rgba(74,107,83,0.6)]"
              aria-label={t("iv.recordAria")}
            >
              <Microphone weight="fill" size={30} />
            </button>
          ) : recording ? (
            <button
              data-testid={INTERVIEW.stopBtn}
              onClick={onStop}
              className="relative h-20 w-20 rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white flex items-center justify-center"
              aria-label={t("iv.stopAria")}
            >
              <Stop weight="fill" size={26} />
            </button>
          ) : (
            <div className="relative h-20 w-20 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center">
              <CheckCircle weight="duotone" size={30} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
            {recording ? t("iv.recording") : blob ? t("iv.recorded") : t("iv.tapMic")}
          </div>
          <div className="mt-1 mono text-3xl">{formatTime(seconds)}</div>
          {recording && (
            <div className="mt-3 flex items-end gap-1 h-6">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="w-1.5 rounded-full bg-[color:var(--brand)] wave-bar"
                  style={{
                    height: `${30 + ((i * 7) % 70)}%`,
                    animationDelay: `${i * 60}ms`,
                  }}
                />
              ))}
            </div>
          )}
          {audioUrl && !recording && (
            <audio
              data-testid={INTERVIEW.playbackBtn}
              controls
              src={audioUrl}
              className="mt-3 w-full"
            />
          )}
          {error && (
            <div className="mt-3 text-sm text-[color:var(--score-bad)]">
              {error}. {t("iv.micAccessDenied")}
            </div>
          )}
        </div>
      </div>

      {blob && !recording && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            data-testid={INTERVIEW.reRecordBtn}
            variant="outline"
            onClick={onReset}
            className="rounded-full border-[color:var(--border-stone)]"
          >
            <ArrowClockwise className="mr-2" size={16} /> {t("iv.rerecord")}
          </Button>
          <Button
            data-testid={INTERVIEW.submitBtn}
            onClick={onSubmit}
            disabled={submitting}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white"
          >
            {submitting ? t("iv.submitting") : t("iv.submit")}
            {!submitting && <ArrowRight className="ml-2" size={16} />}
          </Button>
        </div>
      )}
    </div>
  );
}

function AnsweredView({ answer, t, isFinal = false }) {
  if (isFinal) {
    return (
      <div data-testid="interview-complete-banner">
        <div className="flex items-start gap-4">
          <div className="text-3xl leading-none select-none" aria-hidden="true">
            🎉
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight text-[color:var(--text-primary)]">
              {t("iv.interviewCompleteTitle")}
            </div>
            <p className="mt-2 text-[color:var(--text-secondary)] leading-relaxed max-w-2xl">
              {t("iv.interviewCompleteBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center">
          <CheckCircle weight="duotone" size={22} />
        </div>
        <div>
          <div className="font-semibold tracking-tight">{t("iv.answered")}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">
            {t("iv.answeredSub")}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressRing({ current, total, answered, testId, t }) {
  const size = 44;
  const stroke = 3.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeTotal = Math.max(1, total);
  const clamped = Math.min(Math.max(current, 0), safeTotal);
  const pct = clamped / safeTotal;
  const dashOffset = circumference * (1 - pct);

  return (
    <div
      className="flex items-center gap-3"
      data-testid={testId}
      aria-label={t("iv.progressCurrent", clamped, safeTotal)}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--border-stone)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 400ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tracking-tight text-[color:var(--text-primary)]">
          {clamped}/{safeTotal}
        </div>
      </div>
      <div className="leading-tight hidden sm:block">
        <div className="text-sm text-[color:var(--text-primary)]">
          {t("iv.progressCurrent", clamped, safeTotal)}
        </div>
        <div className="text-[11px] text-[color:var(--text-secondary)]">
          {t("iv.progressAnswered", answered)}
        </div>
      </div>
    </div>
  );
}

function WarmupGate({ question, rec, audioUrl, onContinue, onRecordStart, voice, t }) {
  const audioElRef = useRef(null);
  const audioUrlRef = useRef(null);
  const autoplayedRef = useRef(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);

  const fetchTts = useCallback(async () => {
    if (audioUrlRef.current) return audioUrlRef.current;
    const fd = new FormData();
    fd.append("text", question);
    fd.append("voice", voice || "female");
    const { data } = await api.post(`/tts/warmup`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: TTS_CLIENT_TIMEOUT_MS,
    });
    const bin = atob(data.audio_base64);
    const bytes = new Uint8Array(bin.length);
    for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
    const blob = new Blob([bytes], { type: data.mime || "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    audioUrlRef.current = url;
    return url;
  }, [question, voice]);

  const playQuestion = useCallback(async () => {
    try {
      setTtsLoading(true);
      const url = await fetchTts();
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = url;
        audioElRef.current.currentTime = 0;
        try {
          await audioElRef.current.play();
          setTtsPlaying(true);
        } catch (playErr) {
          // NotAllowedError is Chrome/Safari's autoplay policy blocking
          // the initial mount call — expected before the user gestures
          // on the page. Silently swallow; the "Play question" button
          // remains available and will play on click (user gesture).
          if (!playErr || playErr.name !== "NotAllowedError") {
            console.error("warmup TTS play failed:", playErr);
            toast.error(t("iv.errCreateSession") || "Audio playback failed.");
          }
        }
      }
    } catch (err) {
      console.error("warmup TTS fetch failed:", err);
      toast.error(t("iv.audioUnavailable"));
    } finally {
      setTtsLoading(false);
    }
  }, [fetchTts, t]);

  const stopQuestion = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.currentTime = 0;
    }
    setTtsPlaying(false);
  };

  // Wrap rec.start so any warm-up audio currently playing is silenced
  // immediately — recording always takes priority over TTS playback.
  const startRecording = () => {
    stopQuestion();
    rec.start();
    if (onRecordStart) onRecordStart();
  };

  // Autoplay the warm-up question once, using the selected interviewer
  // voice — mirroring how real interview questions are announced. We kick
  // off the TTS fetch immediately on mount so the audio starts as soon as
  // the OpenAI response arrives. Modern browsers may block this initial
  // play() until the user interacts with the page; that's not an error —
  // the visible "Play question" button remains available and works on
  // first click (a real user gesture).
  useEffect(() => {
    if (autoplayedRef.current) return;
    autoplayedRef.current = true;
    playQuestion();
  }, [playQuestion]);

  // Release the TTS blob URL when the gate unmounts.
  useEffect(() => () => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  return (
    <div className="min-h-screen" data-testid="warmup-gate">
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-8 md:px-12 py-5 flex items-center gap-3">
          <img
            src="/brand/logo.png"
            alt="My English Coach Interview Simulator logo"
            className="h-[63px] w-[63px] object-contain"
          />
          <div className="leading-tight">
            <div className="flex items-center gap-2">
              <span className="font-semibold tracking-tight">My English Coach Interview Simulator</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 md:px-12 py-12 pb-32 sm:pb-12">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-3">
          {t("iv.warmupEye")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.15]"
          data-testid="warmup-title"
        >
          {t("iv.warmupTitle")}
        </h1>
        <p
          className="mt-4 text-[color:var(--text-secondary)] leading-relaxed"
          data-testid="warmup-intro"
        >
          {t("iv.warmupIntro")}
        </p>

        <div className="mt-8 rounded-3xl border border-[color:var(--brand)]/30 bg-[color:var(--brand)]/5 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand)]">
              {t("iv.warmupQuestionEye")}
            </div>
            <button
              type="button"
              data-testid="warmup-play-question"
              onClick={() => (ttsPlaying ? stopQuestion() : playQuestion())}
              disabled={ttsLoading}
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 px-3 py-1.5 text-xs disabled:opacity-50"
            >
              {ttsLoading ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-[color:var(--brand)] animate-pulse" />
                  {t("iv.loading")}
                </>
              ) : ttsPlaying ? (
                <>
                  <PauseCircle weight="fill" size={14} /> {t("iv.stop")}
                </>
              ) : (
                <>
                  <SpeakerHigh weight="fill" size={14} /> {t("iv.playQ")}
                </>
              )}
            </button>
          </div>
          <div
            className="text-xl sm:text-2xl font-medium tracking-tight leading-snug"
            data-testid="warmup-question"
          >
            <Quotes size={22} weight="fill" className="inline-block text-[color:var(--brand)] mr-2 -translate-y-1" />
            {question}
          </div>
          <audio
            ref={audioElRef}
            preload="auto"
            onPlay={() => setTtsPlaying(true)}
            onEnded={() => setTtsPlaying(false)}
            onPause={() => setTtsPlaying(false)}
            className="hidden"
          />
        </div>

        <div className="mt-8 rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-8">
          {rec.error && (
            <div className="mb-4 text-sm text-[color:var(--score-bad)]" data-testid="warmup-error">
              {rec.error}
            </div>
          )}
          <div className="flex items-center gap-4">
            {rec.recording ? (
              <Button
                data-testid="warmup-stop"
                onClick={rec.stop}
                className="rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white px-6"
              >
                <Stop weight="fill" className="mr-2" size={16} />
                {t("iv.stop")} · {formatTime(rec.seconds)}
              </Button>
            ) : rec.blob ? (
              <>
                <audio src={audioUrl} controls className="flex-1" data-testid="warmup-playback" />
                <Button
                  data-testid="warmup-rerecord"
                  variant="outline"
                  onClick={rec.reset}
                  className="rounded-full border-[color:var(--border-stone)]"
                >
                  <ArrowClockwise className="mr-2" size={16} />
                  {t("iv.rerecord")}
                </Button>
              </>
            ) : (
              <Button
                data-testid="warmup-start"
                onClick={startRecording}
                className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6"
              >
                <Microphone weight="fill" className="mr-2" size={16} />
                {t("iv.warmupStart")}
              </Button>
            )}
          </div>
          <p className="mt-4 text-xs text-[color:var(--text-secondary)]">
            {t("iv.warmupBanner")}
          </p>
        </div>

        <div className="mt-8 flex items-center justify-end">
          <Button
            data-testid="warmup-continue"
            onClick={onContinue}
            disabled={!rec.blob || rec.recording}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-8 py-6 text-base disabled:opacity-40"
          >
            {t("iv.warmupContinue")}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </main>

      <div
        data-testid="warmup-mobile-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[color:var(--border-stone)] px-4 py-3 sm:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {rec.recording ? (
          <Button
            data-testid="warmup-mobile-stop"
            onClick={rec.stop}
            className="w-full rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white py-6 text-base"
          >
            <Stop weight="fill" className="mr-2" size={18} />
            {t("iv.stop")} · {formatTime(rec.seconds)}
          </Button>
        ) : rec.blob ? (
          <Button
            data-testid="warmup-mobile-continue"
            onClick={onContinue}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            {t("iv.warmupContinue")}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        ) : (
          <Button
            data-testid="warmup-mobile-start"
            onClick={startRecording}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            <Microphone weight="fill" className="mr-2" size={18} />
            {t("iv.warmupStart")}
          </Button>
        )}
      </div>
    </div>
  );
}
